import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BUYER_TYPES = [
  "Laboratory",
  "Research institution",
  "Educational institution",
  "Chemical reseller",
  "Licensed business buyer",
  "Other approved business use",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", name: "", organization: "",
    buyer_type: "Research institution",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true); setErr("");
    const res = await register(form);
    setBusy(false);
    if (!res.ok) { setErr(res.error); toast.error(res.error); return; }
    toast.success("Account created");
    navigate("/shop");
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="eyebrow">Account</div>
      <h1 className="h-display text-4xl mt-2">Open an account.</h1>
      <p className="text-sm text-[var(--ink-soft)] mt-2">For accredited laboratories and qualified 21+ researchers · account-only checkout · qualification recorded.</p>
      <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
        <Input label="Full name" v={form.name} on={(v) => setForm({ ...form, name: v })} test="register-name" required />
        <Input label="Organization" v={form.organization} on={(v) => setForm({ ...form, organization: v })} test="register-org" />
        <div>
          <label className="eyebrow block mb-1.5">Buyer type</label>
          <select value={form.buyer_type} onChange={(e) => setForm({ ...form, buyer_type: e.target.value })} className="w-full px-4 py-3 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md" data-testid="register-buyer-type">
            {BUYER_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <Input label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} type="email" test="register-email" required />
        <Input label="Password (min 8)" v={form.password} on={(v) => setForm({ ...form, password: v })} type="password" test="register-password" required />
        {err && <div className="text-xs text-[var(--crimson)] font-mono" data-testid="register-error">{err}</div>}
        <button disabled={busy} type="submit" className="btn-primary w-full justify-center disabled:bg-[var(--ink-muted)]" data-testid="register-submit">{busy ? "Creating…" : "Create account"}</button>
      </form>

      <div className="mt-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-[var(--line)]" />
        <span className="text-[10.5px] font-mono uppercase tracking-[0.2em] text-[var(--ink-muted)]">or</span>
        <div className="flex-1 h-px bg-[var(--line)]" />
      </div>

      {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
      <button
        type="button"
        onClick={() => {
          const redirectUrl = window.location.origin + "/auth/callback";
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        }}
        className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-3 border border-[var(--line)] bg-white text-[#3c4043] text-sm font-medium rounded-md hover:bg-[var(--paper-2)] hover:border-[var(--ink)] transition-colors"
        data-testid="google-signup-btn"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>
      <div className="mt-6 text-sm text-[var(--ink-soft)]">
        Have an account? <Link to="/login" className="text-[var(--terra)] font-medium link-line">Sign in</Link>
      </div>
    </div>
  );
}

const Input = ({ label, v, on, type = "text", test, required }) => (
  <div>
    <label className="eyebrow block mb-1.5">{label}</label>
    <input value={v} onChange={(e) => on(e.target.value)} type={type} required={required} className="w-full px-4 py-3 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]" data-testid={test} />
  </div>
);
